import { Link } from 'react-router-dom';
import ContactActions from '../components/ContactActions';
import CtaBand from '../components/CtaBand';
import FamilyTreeArt from '../components/FamilyTreeArt';
import FaqList from '../components/FaqList';
import GuideCard from '../components/GuideCard';
import Icon from '../components/Icon';
import Layout from '../components/Layout';
import { DISCLAIMER_TEXT, HOME_FAQS, PROCESS, PROMISES, SERVICES } from '../constants/content';
import { GUIDES, guidePath } from '../constants/guides';

const QUICK_FIXES = [
  ['Installer stops or will not finish', 'install-family-tree-maker-on-windows'],
  ['Serial number rejected', 'activation-and-serial-number-problems'],
  ['FamilySync stuck or failing', 'fix-familysync-errors'],
  ['Photos show broken-link icons', 'relink-missing-media'],
  ['Ancestry login keeps looping', 'ancestry-sign-in-problems'],
  ['Moving to a new computer', 'move-family-tree-maker-to-new-computer'],
];

const HIGHLIGHTS = [
  {
    img: '/images/whats-timeline.webp',
    title: 'Timelines that tell a life',
    text: 'Births, marriages, moves, and military service lined up in order so gaps in your research stand out.',
  },
  {
    img: '/images/feature-maps.webp',
    title: 'Maps of where they lived',
    text: 'See your ancestors’ journeys plotted across towns, counties, and countries.',
  },
  {
    img: '/images/feature-photo.webp',
    title: 'Photos brought back to life',
    text: 'Attach scans of old portraits and letters to the right person, with built-in photo repair tools.',
  },
];

const FEATURED = ['fix-familysync-errors', 'relink-missing-media', 'backup-and-restore-your-tree'].map((slug) =>
  GUIDES.find((g) => g.slug === slug)
);

export default function HomePage() {
  return (
    <Layout>
      <section className="hero">
        <div className="container hero__grid">
          <div className="hero__copy">
            <p className="eyebrow eyebrow--light">Independent Family Tree Maker Support</p>
            <h1>
              Keep your family story <em>growing</em>, not glitching.
            </h1>
            <p className="hero__lead">
              Friendly technicians for Family Tree Maker installs, activation, FamilySync, TreeVault, and
              missing photos — on Windows and Mac, around the clock.
            </p>
            <ContactActions chatLabel="Chat With Us" light />
            <ul className="hero__ticks">
              <li>
                <Icon name="check" size={18} /> Free guidance first
              </li>
              <li>
                <Icon name="check" size={18} /> FTM 2014 – 2024
              </li>
              <li>
                <Icon name="check" size={18} /> Backup before every fix
              </li>
            </ul>
          </div>
          <div className="hero__art">
            <div className="hero__frame">
              <FamilyTreeArt />
              <p className="hero__caption">Three generations. One safe, organised tree.</p>
            </div>
          </div>
        </div>
        <svg className="hero__wave" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 40c240 40 480 40 720 0s480-40 720 0v40H0Z" />
        </svg>
      </section>

      <section className="section section--tight">
        <div className="container">
          <ul className="promise-row">
            {PROMISES.map((p) => (
              <li key={p.title}>
                <Icon name="shield" size={26} />
                <div>
                  <strong>{p.title}</strong>
                  <span>{p.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">How we help</p>
            <h2>Every branch of Family Tree Maker, covered</h2>
            <p>
              From the first install to the hundredth sync, our technicians know the program inside out
              and explain each step in plain English.
            </p>
          </div>
          <div className="card-grid card-grid--3">
            {SERVICES.map((s) => (
              <article key={s.title} className="service-card">
                <span className="service-card__icon">
                  <Icon name={s.icon} size={26} />
                </span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
          <p className="center">
            <Link to="/support-services" className="link-arrow">
              See all support services <Icon name="arrow" size={18} />
            </Link>
          </p>
        </div>
      </section>

      <section className="section section--parchment">
        <div className="container split">
          <div className="split__media">
            <img src="/images/portraits.webp" alt="Old family portraits arranged on a table" width="640" height="480" loading="lazy" />
          </div>
          <div className="split__copy">
            <p className="eyebrow">Why it matters</p>
            <h2>Decades of research deserve more than a shoebox</h2>
            <p>
              Every family has stories one generation away from being forgotten — the great-grandmother
              who crossed an ocean, the surname that changed at the border, the farm that stayed in the
              family for a century. Family Tree Maker turns those fragments into a searchable record of
              people, places, photos, and the sources that prove them.
            </p>
            <p>
              It is powerful software, and like all software it sometimes misbehaves. When it does, we are
              here so a technical glitch never stands between you and your family history.
            </p>
            <Link to="/about-us" className="link-arrow">
              Learn about us <Icon name="arrow" size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Simple process</p>
            <h2>What happens when you reach out</h2>
          </div>
          <ol className="process">
            {PROCESS.map((step, i) => (
              <li key={step.title}>
                <span className="process__num">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section section--forest">
        <div className="container split split--reverse">
          <div className="split__copy">
            <p className="eyebrow eyebrow--light">Common problems</p>
            <h2>The fixes people ask us for most</h2>
            <p>Each link opens a free, step-by-step guide. Prefer a person? Open live chat and we will walk through it with you.</p>
            <ContactActions light />
          </div>
          <ul className="fix-list">
            {QUICK_FIXES.map(([label, slug]) => (
              <li key={slug}>
                <Link to={guidePath(slug)}>
                  <Icon name="leaf" size={20} />
                  <span>{label}</span>
                  <Icon name="arrow" size={18} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Inside the software</p>
            <h2>Tools that make family history come alive</h2>
            <p>
              Whether you are on <Link to="/family-tree-maker-2024">Family Tree Maker 2024</Link> or the
              long-loved <Link to="/family-tree-maker-2019">2019 edition</Link>, we will help you get the
              most out of features like these.
            </p>
          </div>
          <div className="card-grid card-grid--3">
            {HIGHLIGHTS.map((h) => (
              <article key={h.title} className="img-card">
                <img src={h.img} alt="" width="640" height="427" loading="lazy" />
                <div className="img-card__body">
                  <h3>{h.title}</h3>
                  <p>{h.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--parchment">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Free guides</p>
            <h2>Learn it yourself, at your own pace</h2>
          </div>
          <div className="card-grid card-grid--3">
            {FEATURED.map((g) => (
              <GuideCard key={g.slug} guide={g} />
            ))}
          </div>
          <p className="center">
            <Link to="/guides" className="btn btn--outline">
              Browse the guide library
            </Link>
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container container--narrow">
          <div className="section-head">
            <p className="eyebrow">Quick answers</p>
            <h2>Frequently asked questions</h2>
          </div>
          <FaqList items={HOME_FAQS} />
          <p className="center">
            <Link to="/faqs" className="link-arrow">
              See all FAQs <Icon name="arrow" size={18} />
            </Link>
          </p>
        </div>
      </section>

      <CtaBand />

      <section className="section section--tight">
        <div className="container container--narrow">
          <div className="disclaimer">
            <h2>Disclaimer</h2>
            <p>{DISCLAIMER_TEXT}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
